/**
 * The Twilio `Device`, and the one decision that made it worth its own file:
 * whether it lives for the length of a call or for the length of the session.
 *
 * Outbound only ever needed the first. A `Device` was built when the operator
 * pressed dial and destroyed when they hung up, so an operator who never called
 * anybody paid nothing - not the SDK download, not a websocket.
 *
 * Receiving a call needs the second. Twilio can only route an inbound `<Client>`
 * leg to an identity that is REGISTERED right now, so the browser has to be
 * holding a connection open before the phone rings, which is to say for as long
 * as the CRM is on screen. That is a real cost, so it is paid only where it buys
 * something: the token endpoint answers `incoming: false` when inbound calling
 * is switched off, and then nothing is created until somebody dials.
 *
 * Both modes share one object either way. Two `Device`s on the same identity
 * would leave Twilio choosing which of them gets the call - which is not a
 * hypothetical: it is what stopped the phone ringing on 2026-08-11, when three
 * CRM windows were open at once. `useDeviceLease` is what keeps it to one, and
 * `reach` below is how the operator can see which window that is.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { Call, Device } from "@twilio/voice-sdk";
import { getVoiceToken } from "../../../lib/calls";
import { applySdkRingSounds, releaseAudioInput, type CallAudio } from "../audio/apply";
import { releaseMicGain } from "../audio/gain";
import { forgetRemoteAudio } from "../audio/speaker";
import { createTokenRenewer, isStaleTokenError, TOKEN_REFRESH_LEAD_MS } from "./refresh";
import { setRingSource } from "../incoming/ringSource";
import { hasCustomRingback, setRingbackSource } from "../outgoing/ringbackSource";
import { useCallPresence } from "./useCallPresence";
import { useDeviceLease } from "./useDeviceLease";

type Sdk = typeof import("@twilio/voice-sdk");

/**
 * Whether a call arriving right now would ring in THIS window, and if not, why.
 *
 * A boolean was not enough. "Not registered" covers four situations that call for
 * four different reactions - inbound is switched off account-wide, the connection
 * is still being made, another CRM window is the one that rings, or registration
 * is failing and somebody has to look at it - and reporting them as one silent
 * `false` is how a browser can sit there looking connected while the phone rings
 * nowhere.
 */
export type VoiceReachability =
  /** Inbound calling is off, or telephony is not configured for this operator. */
  | "off"
  /** Building the device or waiting for Twilio to accept the registration. */
  | "connecting"
  /** Registered. A call to the office rings here. */
  | "ready"
  /** Another CRM window in this browser holds the registration. */
  | "elsewhere"
  /** Registration will not take. Retrying, but somebody should look. */
  | "failed";

export interface VoiceDeviceOptions {
  /** The operator may use telephony at all (permission gate). */
  enabled: boolean;
  /** Twilio is offering an inbound call to this browser. */
  onIncoming: (call: Call) => void;
  /** A device-level failure with no call to attach it to. */
  onError: (code: number | undefined) => void;
  /**
   * A call is up in this window. Read as a function so the lease sees the truth
   * at the moment another window claims it, not at the last render - handing the
   * registration over mid-call would destroy the `Device` carrying it.
   */
  busy: () => boolean;
}

export interface VoiceDeviceHandle {
  /**
   * A `Device` ready to place a call. Reuses the registered one when there is
   * one, otherwise builds a throwaway.
   */
  acquire: () => Promise<Device>;
  /** The call is over. Destroys a throwaway device; leaves a registered one. */
  release: () => void;
  /**
   * Twilio's audio routing for whichever device exists now, or null.
   *
   * A function rather than a value: the `Device` is built and destroyed several
   * times a day and its `AudioHelper` goes with it, so a stored reference would
   * be a routing object for a device that no longer carries any audio.
   */
  audio: () => CallAudio | null;
  /** Whether this window can be rung, and what is in the way when it cannot. */
  reach: VoiceReachability;
}

/**
 * How long to wait before trying a refused registration again.
 *
 * Doubling from a second, then half a minute for as long as it takes. The last
 * rung repeating is the point: the first version tried exactly once and left the
 * browser deaf until somebody reloaded the page, and the second gave the ladder
 * an end - which is the same failure with a minute's delay on it, because
 * nothing fires `unregistered` at a device that is already unregistered. The
 * causes are all things that pass on their own: an API restarting during a
 * deploy, a laptop lid, a token being renewed in parallel by `refresh.ts`.
 */
const RETRY_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000, 16_000];

/** Where the ladder settles, and stays. */
const RETRY_MAX_MS = 30_000;

/** The rung for attempt `n`; everything past the ladder waits the cap. */
export function backoff(n: number): number {
  return RETRY_MS[n] ?? RETRY_MAX_MS;
}

/**
 * Take both ring addresses off a token response.
 *
 * Every path that fetches a token goes through here, and that is the point.
 * The registration path is the obvious one, but an installation with inbound
 * switched off never registers at all - it builds a throwaway device when
 * somebody dials - and before this was shared, that installation could never
 * learn its own outgoing ring. The renewal path is the third: a token is
 * replaced roughly hourly, so this is also what makes a ring chosen in Settings
 * reach a browser that has been open all day.
 */
function adoptRingSources(token: { ringtoneUrl?: string; ringbackUrl?: string }): void {
  setRingSource(token.ringtoneUrl);
  setRingbackSource(token.ringbackUrl);
}

export function useVoiceDevice(opts: VoiceDeviceOptions): VoiceDeviceHandle {
  const { enabled, onIncoming, onError, busy } = opts;
  const deviceRef = useRef<Device | null>(null);
  /** True when the device above is the session-long one, not a per-call one. */
  const persistentRef = useRef(false);
  const [reach, setReach] = useState<VoiceReachability>("off");
  // Two votes, in order. The first settles which window of THIS browser profile
  // should hold the telephone; the second settles which machine, because a
  // BroadcastChannel does not reach the laptop in the other room. A window that
  // has already lost locally does not beat at all - a background tab has no
  // business bidding against another computer.
  const localHeld = useDeviceLease({ enabled, busy });
  const mine = useCallPresence({
    enabled: enabled && localHeld,
    // Folded to the three words the lease understands. `failed` is the retry
    // ladder running, which is the one state that should cost this window the
    // telephone; everything short of `ready` is merely "not yet", including the
    // `elsewhere` a window sits in precisely BECAUSE it does not hold the lease.
    reach: reach === "ready" ? "ready" : reach === "failed" ? "failed" : "trying",
    busy,
  });
  const held = localHeld && mine;

  // Handlers are read through refs so the registration effect below can depend
  // on `enabled` alone. Without this, a parent re-render that produced a new
  // callback identity would tear the registration down and build it again -
  // and a browser that re-registers every few seconds is a browser that misses
  // the call that arrives in between.
  const incomingRef = useRef(onIncoming);
  const errorRef = useRef(onError);
  useEffect(() => {
    incomingRef.current = onIncoming;
    errorRef.current = onError;
  }, [onIncoming, onError]);

  const destroy = useCallback(() => {
    try {
      deviceRef.current?.destroy();
    } catch {
      // Already destroyed, or never connected.
    }
    deviceRef.current = null;
    persistentRef.current = false;
  }, []);

  const build = useCallback(async (sdk: Sdk, token: string, edge: string): Promise<Device> => {
    const device = new sdk.Device(token, {
      ...(edge ? { edge } : {}),
      logLevel: "error",
      // NOT the SDK's default, which is TEN SECONDS - see refresh.ts. Ten
      // seconds is not enough for an authenticated round trip, and in a
      // background tab, where a timer may be held back by a minute, it means
      // the warning arrives after the token it was warning about has died.
      tokenRefreshMs: TOKEN_REFRESH_LEAD_MS,
      // NOT the SDK's default either. With `false` a second offer during a call
      // is dropped inside the SDK without a word: no `incoming` event, no
      // reject, and the caller's leg rings out as `no-answer` (measured in
      // prod, 2026-11-01). Let through, the provider decides - a knock, or a
      // refusal the server hears at once (see `waiting/`).
      allowIncomingWhileBusy: true,
    });

    /**
     * One renewer for this Device's whole life, shared by the two ways a token
     * gets replaced - the warning ahead of time, and the errors that mean the
     * warning did not do its job. Serialised and rate limited inside, which is
     * what makes it safe on the error path: a refused transport re-emits
     * several times a second.
     */
    const renew = createTokenRenewer({
      fetch: async () => {
        const next = await getVoiceToken();
        // A browser left open all day would otherwise ring with whatever the
        // settings said when it was first loaded. The SDK's own sounds are
        // re-applied with them, because "silence theirs" and "play ours" are
        // one decision and half of it going stale is a doubled ring.
        adoptRingSources(next);
        applySdkRingSounds(device.audio as CallAudio | null, hasCustomRingback());
        return next.token;
      },
      apply: (next) => device.updateToken(next),
      alive: () => device.state !== "destroyed",
    });

    device.on("error", (err: { code?: number }) => {
      // THE RECOVERY THAT WAS MISSING. A lapsed token does not merely stop new
      // calls: Twilio refuses the SDK's own websocket with 20101, and every
      // message it tries to send afterwards fails with 31009 - while the audio,
      // which does not go through that socket, carries on. Without this the
      // browser stays in that state until somebody reloads the page.
      if (isStaleTokenError(err?.code)) void renew();
      errorRef.current(err?.code);
    });

    // The ordinary path: replace the token before it lapses. Refreshing in
    // place keeps the registration alive; letting it lapse would silently stop
    // the phone ringing while the CRM still looks perfectly connected.
    device.on("tokenWillExpire", () => {
      void renew();
    });

    // ONE RING PER CALL, IN BOTH DIRECTIONS. The SDK plays a ringtone of its
    // own for a call coming in and another for one going out, and this front
    // end has a sound for both - so before this line a call to the office rang
    // twice, out of two output paths, and choosing a device for one of them
    // would still have left the other on the laptop speaker.
    //
    // Read here rather than passed in, and read at BUILD time on purpose: the
    // address arrives with the token, `adoptRingSources` has already run on
    // every path that reaches this, and a device rebuilt after a settings
    // change picks up the new answer with it. See `audio/apply.ts`.
    applySdkRingSounds(device.audio as CallAudio | null, hasCustomRingback());
    return device;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setReach("off");
      return;
    }
    // Another window is the one that rings. Nothing is built here, and nothing
    // stays registered either - the whole point of the lease is that Twilio has
    // exactly one place to send the call.
    if (!held) {
      setReach("elsewhere");
      if (persistentRef.current) destroy();
      return;
    }

    let cancelled = false;
    setReach("connecting");

    void (async () => {
      let token;
      try {
        token = await getVoiceToken();
      } catch {
        // Telephony not configured, or no permission. Outbound will report it
        // properly when somebody dials; there is nothing to say before then.
        if (!cancelled) setReach("off");
        return;
      }
      if (cancelled) return;
      // Recorded before the registration rather than after, so the sounds are
      // already decided by the time the first call can arrive.
      adoptRingSources(token);
      // Inbound switched off account-wide. Not a failure and not worth retrying:
      // the token endpoint said so, and it will say otherwise after a reload.
      if (!token.incoming) {
        setReach("off");
        return;
      }

      const sdk = await import("@twilio/voice-sdk");
      if (cancelled) return;

      const device = await build(sdk, token.token, token.edge);
      if (cancelled) {
        device.destroy();
        return;
      }
      device.on("incoming", (call: Call) => incomingRef.current(call));
      deviceRef.current = device;
      persistentRef.current = true;

      /**
       * Register, and keep trying.
       *
       * The previous version made one attempt and swallowed the failure, which
       * left a CRM that could place calls and could not receive them with
       * nothing on screen to say so. Registration is also not a one-off event:
       * the SDK drops it on a lost websocket and on an expired token, and
       * `unregistered` below brings this back rather than leaving the browser
       * quietly unreachable for the rest of the day.
       */
      /**
       * ONE LADDER AT A TIME.
       *
       * `unregistered` is not one edge per outage: the SDK emits it while it
       * tears the stream down, and again on its way through a reconnect. An
       * unguarded handler therefore ADDS ladders rather than restarting one,
       * and since `register()` throws `InvalidStateError` unless the device is
       * currently unregistered, every loop but the winner is stuck refusing
       * itself forever - each one reporting "failed" every thirty seconds
       * against a device that is registered and perfectly reachable.
       */
      let laddering = false;
      const attempt = async () => {
        if (laddering) return;
        laddering = true;
        try {
          for (let n = 0; ; n++) {
            if (cancelled || device.state === "destroyed") return;
            // Somebody else already got there - the SDK re-registers itself on
            // reconnect. Not a failure, and not something to report as one.
            if (device.state === "registered") return;
            try {
              await device.register();
              return;
            } catch {
              // Reported on every failure, and then tried again: "failed" is
              // what the CRM shows when a call to the office would ring
              // nowhere, and the operator needs to see that for as long as it
              // is true - but giving up on it is not the same thing as
              // reporting it. This is also the loop that picks the registration
              // back up once `refresh.ts` has replaced a token Twilio was
              // refusing, which is the only thing standing between a lapsed
              // token and a browser that stays deaf until somebody reloads.
              setReach("failed");
              await new Promise((r) => setTimeout(r, backoff(n)));
            }
          }
        } finally {
          laddering = false;
        }
      };

      device.on("registered", () => {
        if (!cancelled) setReach("ready");
      });
      device.on("unregistered", () => {
        // Also fires on `destroy()`, which is why the guards are on the state
        // and not just on `cancelled`: a torn-down device must not start a
        // retry loop on its way out.
        if (cancelled || device.state === "destroyed") return;
        setReach("connecting");
        void attempt();
      });

      await attempt();
    })();

    return () => {
      cancelled = true;
      if (persistentRef.current) destroy();
    };
  }, [enabled, held, build, destroy]);

  const acquire = useCallback(async (): Promise<Device> => {
    const existing = deviceRef.current;
    if (existing) return existing;
    const [token, sdk] = await Promise.all([getVoiceToken(), import("@twilio/voice-sdk")]);
    // BEFORE `build`, which reads them. This is the only path an installation
    // with inbound switched off ever takes, so leaving it out would mean the
    // outgoing ring worked everywhere except where it is the only ring there is.
    adoptRingSources(token);
    const device = await build(sdk, token.token, token.edge);
    deviceRef.current = device;
    persistentRef.current = false;
    return device;
  }, [build]);

  const release = useCallback(() => {
    const audioHelper = deviceRef.current?.audio as CallAudio | null;
    // The gain node before the capture, because taking it off asks the SDK to
    // re-acquire the microphone and there is no point re-acquiring one that is
    // about to be let go. This is also the ONLY place it comes off: mid-call
    // the level is turned rather than unfitted, so that a slider dragged back
    // across 100% does not cut a hole in the conversation (see `audio/gain.ts`).
    void releaseMicGain(audioHelper);
    // The microphone next, and only a chosen one was ever held: Twilio keeps a
    // `setInputDevice` capture open until it is told otherwise, so without this
    // the tab would sit between calls with its recording indicator lit for
    // every operator who has picked a headset.
    void releaseAudioInput(audioHelper);
    // The elements the far end was coming out of are the SDK's, and it builds
    // new ones for the next call. Holding these would be holding dead nodes.
    forgetRemoteAudio();
    // A registered device outlives the call it just carried; a throwaway does
    // not, and leaving it alive would hold a second connection open for nothing.
    if (!persistentRef.current) destroy();
  }, [destroy]);

  const audio = useCallback(() => (deviceRef.current?.audio as CallAudio | null) ?? null, []);

  return { acquire, release, audio, reach };
}
